import { useEffect, useRef, useCallback, useState } from 'react'
import { getToken } from '../auth/sso.js'

// STOMP-over-WebSocket connection with polling fallback
export function useWebSocket(workspaceId, { onSnapshot, onTelemetry, fallbackFn, pollInterval = 5000 } = {}) {
  const stompRef = useRef(null)
  const pollTimerRef = useRef(null)
  const [connected, setConnected] = useState(false)
  const [usingFallback, setUsingFallback] = useState(false)

  const startPolling = useCallback(() => {
    if (!fallbackFn || pollTimerRef.current) return
    setUsingFallback(true)
    pollTimerRef.current = setInterval(() => {
      fallbackFn()
    }, pollInterval)
  }, [fallbackFn, pollInterval])

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    setUsingFallback(false)
  }, [])

  useEffect(() => {
    if (!workspaceId) return

    let active = true

    const connect = async () => {
      try {
        const { Client } = await import('@stomp/stompjs')

        const stomp = new Client({
          // The server authenticates the CONNECT frame — the browser
          // WebSocket API cannot set headers on the handshake itself.
          connectHeaders: { Authorization: `Bearer ${getToken() || ''}` },
          brokerURL: (() => {
            const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
            const host = window.location.port
              ? `${window.location.hostname}:${window.location.port}`
              : window.location.hostname
            // /ws is SockJS; /ws-raw is the native WebSocket endpoint
            return `${scheme}://${host}/ws-raw`
          })(),
          reconnectDelay: 5000,
          onConnect: () => {
            if (!active) return
            setConnected(true)
            stopPolling()

            stomp.subscribe(`/topic/workspace/${workspaceId}`, (message) => {
              if (!active) return
              try {
                const event = JSON.parse(message.body)
                if (event.type === 'SNAPSHOT' && onSnapshot) {
                  onSnapshot(event.data)
                } else if (event.type === 'TELEMETRY' && onTelemetry) {
                  onTelemetry(event.data)
                }
              } catch {
                // Malformed message — ignore
              }
            })
          },
          onDisconnect: () => {
            if (!active) return
            setConnected(false)
            startPolling()
          },
          onStompError: () => {
            if (!active) return
            setConnected(false)
            startPolling()
          },
        })

        stomp.activate()
        stompRef.current = stomp
      } catch {
        // STOMP unavailable — use polling
        if (active) startPolling()
      }
    }

    connect()

    return () => {
      active = false
      stopPolling()
      if (stompRef.current) {
        stompRef.current.deactivate()
        stompRef.current = null
      }
      setConnected(false)
    }
  }, [workspaceId, onSnapshot, onTelemetry, startPolling, stopPolling])

  return { connected, usingFallback }
}
